
import React from 'react'
import {Field} from 'redux-form'
import {Tag, Upload} from 'antd'
import {InputField, SelectField, TextAreaField, CheckboxField} from 'FormFields'
import {UploadOutlined} from '@ant-design/icons'
import LogoStyles from './LogoStyle.css';
import { CKEditor } from 'ckeditor4-react';
import { debounce } from 'lodash';

const labelColSpan = 6
const wrapperColSpan = 14

type Props = {
    id: ?any,
    type: string,
    processor_json: string,
    value: string,
    title: string,
    processor: string,
    validation_json: string,
    submitting: boolean,
    logo: string,
    onUpload: Function,
    change: Function,
    formValues: Object,
}

//todo - move to common area
const RichTextEditorField = React.memo(({ input }) => {
    const handleChange = React.useCallback(
        debounce((evt) => {
            input.onChange(evt.editor.getData());
        }, 300),
        [input]
    );

    return (
        <CKEditor
            type="classic"
            config={{ versionCheck: false }}
            initData={input.value}
            onChange={handleChange}
        />
    );
});

export default class extends React.PureComponent<Props> {
    constructor(props) {
        super(props);

        // Initialize rowCount based on existing values if present
        let initialRowCount = 1;

        // Handle different data formats
        if (props.value) {
            if (Array.isArray(props.value)) {
                // Direct array format
                initialRowCount = Math.max(1, props.value.length);
                console.log('Setting form initialized with array data, row count:', initialRowCount);
            } else if (typeof props.value === 'object' && props.value !== null) {
                // Object format (might be from JSON)
                const valueKeys = Object.keys(props.value);
                if (valueKeys.length > 0) {
                    // Try to determine if it's an indexed object (like {0: {...}, 1: {...}})
                    const numericKeys = valueKeys.filter(k => !isNaN(parseInt(k)));
                    if (numericKeys.length > 0) {
                        initialRowCount = Math.max(1, Math.max(...numericKeys.map(k => parseInt(k))) + 1);
                        console.log('Setting form initialized with object data, row count:', initialRowCount);
                    }
                }
            } else if (typeof props.value === 'string') {
                // Try to parse JSON string
                try {
                    const parsedValue = JSON.parse(props.value);
                    if (Array.isArray(parsedValue)) {
                        initialRowCount = Math.max(1, parsedValue.length);
                        console.log('Setting form initialized with parsed JSON array, row count:', initialRowCount);
                    } else if (typeof parsedValue === 'object' && parsedValue !== null) {
                        const valueKeys = Object.keys(parsedValue);
                        if (valueKeys.length > 0) {
                            const numericKeys = valueKeys.filter(k => !isNaN(parseInt(k)));
                            if (numericKeys.length > 0) {
                                initialRowCount = Math.max(1, Math.max(...numericKeys.map(k => parseInt(k))) + 1);
                                console.log('Setting form initialized with parsed JSON object, row count:', initialRowCount);
                            }
                        }
                    }
                } catch (e) {
                    // Not valid JSON, keep default
                    console.log('Setting form value is not valid JSON:', e.message);
                }
            }
        }

        this.state = {
            rowCount: initialRowCount
        };

        console.log('Setting form initialized with row count:', initialRowCount);
    }

    componentDidUpdate(prevProps) {
        // Update rowCount when value prop changes
        if (prevProps.value !== this.props.value) {
            let newRowCount = 1;
            const { value } = this.props;

            // Handle different data formats - same logic as constructor
            if (value) {
                if (Array.isArray(value)) {
                    // Direct array format
                    newRowCount = Math.max(1, value.length);
                    console.log('Setting form updated with array data, row count:', newRowCount);
                } else if (typeof value === 'object' && value !== null) {
                    // Object format (might be from JSON)
                    const valueKeys = Object.keys(value);
                    if (valueKeys.length > 0) {
                        // Try to determine if it's an indexed object (like {0: {...}, 1: {...}})
                        const numericKeys = valueKeys.filter(k => !isNaN(parseInt(k)));
                        if (numericKeys.length > 0) {
                            newRowCount = Math.max(1, Math.max(...numericKeys.map(k => parseInt(k))) + 1);
                            console.log('Setting form updated with object data, row count:', newRowCount);
                        }
                    }
                } else if (typeof value === 'string') {
                    // Try to parse JSON string
                    try {
                        const parsedValue = JSON.parse(value);
                        if (Array.isArray(parsedValue)) {
                            newRowCount = Math.max(1, parsedValue.length);
                            console.log('Setting form updated with parsed JSON array, row count:', newRowCount);
                        } else if (typeof parsedValue === 'object' && parsedValue !== null) {
                            const valueKeys = Object.keys(parsedValue);
                            if (valueKeys.length > 0) {
                                const numericKeys = valueKeys.filter(k => !isNaN(parseInt(k)));
                                if (numericKeys.length > 0) {
                                    newRowCount = Math.max(1, Math.max(...numericKeys.map(k => parseInt(k))) + 1);
                                    console.log('Setting form updated with parsed JSON object, row count:', newRowCount);
                                }
                            }
                        }
                    } catch (e) {
                        // Not valid JSON, keep default
                        console.log('Setting form value is not valid JSON:', e.message);
                    }
                }
            }

            // Only update state if the row count has changed
            if (newRowCount !== this.state.rowCount) {
                this.setState({ rowCount: newRowCount });
                console.log('Setting form row count updated to:', newRowCount);
            }
        }
    }

    addRow = () => {
        this.setState(prevState => ({
            rowCount: prevState.rowCount + 1
        }), () => {
            // After state update, set values for any autoincrement ID fields in the new row
            const { processor_json } = this.props;
            let processorJsonObj = null;

            // Parse processor_json if it exists and is a string
            if (processor_json !== undefined && processor_json !== "") {
                try {
                    processorJsonObj = JSON.parse(processor_json);
                } catch (e) {
                    console.error('Error parsing processor_json:', e);
                    return;
                }
            }

            /*if (processorJsonObj && processorJsonObj.columnNames) {
                const idColumn = processorJsonObj.columnNames.find(col =>
                    col.key === 'id' && col.autoincrement === true
                );

                if (idColumn) {
                    const newRowIndex = this.state.rowCount - 1;
                    const fieldName = `value[${newRowIndex}][id]`;
                    const fieldValue = newRowIndex + 1;

                    // Use setTimeout to ensure the field is rendered before setting value
                    setTimeout(() => {
                        const form = this.props.form;
                        if (form && form.change) {
                            form.change(fieldName, fieldValue);
                        }
                    }, 50);
                }
            }*/
        });
    }

    removeRow = (rowIndex) => {
        // Prevent removing the last row
        if (this.state.rowCount <= 1) {
            return;
        }

        // Get the current form values from Redux Form
        const { formValues } = this.props;
        const currentValue = formValues && formValues.value ? formValues.value : this.props.value;
        
        if (!currentValue) {
            // If no current value, just update the row count
            this.setState(prevState => ({ rowCount: prevState.rowCount - 1 }));
            return;
        }

        let parsedValue = currentValue;

        // Handle string values (JSON strings)
        if (typeof currentValue === 'string' && currentValue.trim() !== '') {
            try {
                parsedValue = JSON.parse(currentValue);
            } catch (e) {
                console.error('Error parsing value as JSON:', e);
                return;
            }
        }

        // Create the new array without the removed row
        let newValues = [];

        if (Array.isArray(parsedValue)) {
            newValues = parsedValue.filter((_, index) => index !== rowIndex);
        } else if (typeof parsedValue === 'object' && parsedValue !== null) {
            // Convert object to array, removing the specified index
            const keys = Object.keys(parsedValue).sort((a, b) => parseInt(a) - parseInt(b));
            newValues = [];
            let newIndex = 0;
            
            keys.forEach(key => {
                const keyIndex = parseInt(key);
                if (keyIndex !== rowIndex) {
                    newValues[newIndex] = parsedValue[key];
                    newIndex++;
                }
            });
        }

        // Update the form field value using Redux Form's change method
        // This is accessed through the form prop that Redux Form provides
        if (this.props.change) {
            this.props.change('value', newValues);
        }

        // Update the row count in state
        this.setState(prevState => ({ rowCount: prevState.rowCount - 1 }), () => {
            // Handle auto-incrementing ID fields if needed
            const { processor_json } = this.props;
            let processorJsonObj = null;

            if (processor_json !== undefined && processor_json !== "") {
                try {
                    processorJsonObj = JSON.parse(processor_json);
                } catch (e) {
                    console.error('Error parsing processor_json:', e);
                    return;
                }
            }

            // Update auto-incrementing ID fields for remaining rows
            if (processorJsonObj && processorJsonObj.columnNames) {
                const idColumn = processorJsonObj.columnNames.find(col =>
                    col.key === 'id' && col.autoincrement === true
                );

                if (idColumn && this.props.change) {
                    // Update ID fields for all remaining rows to maintain sequential numbering
                    for (let i = 0; i < this.state.rowCount; i++) {
                        const fieldName = `value[${i}][id]`;
                        this.props.change(fieldName, i + 1);
                    }
                }
            }
        });
    }

    render() {

        const {
            id,
            type,
            processor,
            processor_json,
            title,
            validation_json,
            submitting,
            logo,
            onUpload,
            value,
        } = this.props

        let imgValue;
        imgValue = "";
        if (processor == "FileUpload" && (value != undefined || value != ""))
            imgValue = "/settings/files/" + value;

        let _processor_json;
        if (processor_json != undefined && processor_json != "")
        {
            _processor_json = JSON.parse(processor_json);
        }
        else if (processor == 'SelectField')
            _processor_json = [{"value": "", "label": "DEFAULT"}];

        // Generate column headers once for all rows
        function getColumnHeaders() {
            let headers = [];
            if (_processor_json && _processor_json.columnNames) {
                for (let ccount = 0; ccount < _processor_json.columnNames.length; ccount++) {
                    const column = _processor_json.columnNames[ccount];
                    const isIdWithAutoincrement = column.key === 'id' && column.autoincrement === true;

                    // Calculate field width for ID fields with autoincrement
                    const fieldStyle = isIdWithAutoincrement ? {
                        flex: '0 0 auto',
                        width: `${Math.max(40, String(10).length * 20)}px`
                    } : { flex: 1 };

                    headers.push(
                        <div style={fieldStyle} key={`header-${column.key}`}>
                            <div style={{
                                fontWeight: 'normal',
                                marginBottom: '5px',
                                fontSize: '12px',
                                padding: '0 0 8px',
                                lineHeight: '1.5715',
                                whiteSpace: 'initial',
                                textAlign: 'left'
                            }}>
                                {column.value}
                            </div>
                        </div>
                    );
                }
            }
            return headers;
        }

        function getAssociatedRowFields (rowNumber)
        {
            const that = this; // Store reference to component instance
            let output = [];
            for (let ccount = 0; ccount < _processor_json.columnNames.length; ccount ++)
            {
                const column = _processor_json.columnNames[ccount];
                const isIdWithAutoincrement = column.key === 'id' &&
                    column.autoincrement === true;

                // Check if this field has a SelectField processor
                const isSelectField = column.processor === 'SelectField';

                // Calculate field width for ID fields with autoincrement
                // Apply any custom styles from the column config if available
                let fieldStyle = isIdWithAutoincrement ? {
                    flex: '0 0 auto',
                    width: `${Math.max(40, String(rowNumber + 1).length * 20)}px`
                } : { flex: 1 };

                // Apply any style object from the column config
                if (column.style && typeof column.style === 'object') {
                    fieldStyle = { ...fieldStyle, ...column.style };
                }

                // For ID fields with autoincrement, use the row number + 1 as the value if no existing value
                // This ensures we preserve any existing IDs from the database
                const fieldValue = isIdWithAutoincrement ? (rowNumber + 1) : undefined;

                // Get select options if this is a SelectField
                let selectOptions = [];
                if (isSelectField && column.options) {
                    try {
                        if (typeof column.options === 'string') {
                            selectOptions = JSON.parse(column.options);
                        } else if (Array.isArray(column.options)) {
                            selectOptions = column.options;
                        }
                    } catch (e) {
                        console.error('Error parsing select options:', e);
                    }
                }

                output.push(
                    <div style={fieldStyle} key={`row-${rowNumber}-col-${column.key}`}>
                        {isIdWithAutoincrement ? (
                            // For ID fields with autoincrement, use a display-only field

                             <Field
                             component={InputField}
                             name={"value[" + rowNumber + "][" + column.key + "]"}
                             label=""
                             disabled={submitting}
                             labelPosition="top"
                             labelCol={{ span: 0 }}
                             initialValue={fieldValue}
                             defaultValue={fieldValue}
                             style={column.style || {}}
                             fieldStyle={column.style || {}}
                             labelStyle={column.style || {}}
                            />

                        ) : isSelectField ? (
                            <Field
                                component={SelectField}
                                name={"value[" + rowNumber + "][" + column.key + "]"}
                                label=""
                                disabled={submitting}
                                labelPosition="top"
                                labelCol={{ span: 0 }}
                                options={selectOptions}
                                placeholder={column.placeholder || `Select ${column.value}`}
                                allowClear={true}
                                showSearch={true}
                                style={column.style || {}}
                                fieldStyle={column.style || {}}
                                labelStyle={column.style || {}}
                            />
                        ) : (
                            <Field
                                component={InputField}
                                name={"value[" + rowNumber + "][" + column.key + "]"}
                                label=""
                                disabled={submitting}
                                labelPosition="top"
                                labelCol={{ span: 0 }}
                                style={column.style || {}}
                                fieldStyle={column.style || {}}
                                labelStyle={column.style || {}}
                            />
                        )}
                    </div>
                )
            }
            return output;
        }

        const getAssociatedRow = (rowNumber) => {
            let output = [];
            const that = this; // Reference to the component instance
            const canRemove = this.state.rowCount > 1; // Only allow removal if more than one row exists

            if (typeof _processor_json != "undefined") {
                output.push(
                    <div style={{ display: 'flex', marginBottom: '10px' }} key={`row-${rowNumber}`}>
                        <div style={{ display: 'flex', flex: 1 }}>
                            {getAssociatedRowFields(rowNumber)}
                        </div>
                        <div style={{ marginLeft: '10px', display: 'flex', alignItems: 'center' }}>
                            <button
                                type="button"
                                onClick={() => that.removeRow(rowNumber)}
                                disabled={!canRemove}
                                style={{
                                    padding: '4px 10px',
                                    backgroundColor: canRemove ? '#ff4d4f' : '#f5f5f5',
                                    color: canRemove ? 'white' : '#d9d9d9',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: canRemove ? 'pointer' : 'not-allowed',
                                    fontSize: '12px'
                                }}
                                title={canRemove ? 'Remove this row' : 'Cannot remove the last row'}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                );
            } else {
                output.push(
                    <div style={{ display: 'flex', marginBottom: '10px' }} key={`row-${rowNumber}`}>
                        <div style={{ display: 'flex', flex: 1 }}>
                            <div style={{ flex: 1 }}>
                                <Field
                                    component={InputField}
                                    name={"value[" + rowNumber + "][key]"}
                                    label={"Key " + (rowNumber+1)}
                                    disabled={submitting}
                                    labelCol={{span: labelColSpan}}
                                    wrapperCol={{span: wrapperColSpan}}
                                    style={_processor_json && _processor_json.style || {}}
                                    fieldStyle={_processor_json && _processor_json.style || {}}
                                    labelStyle={_processor_json && _processor_json.style || {}}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Field
                                    component={InputField}
                                    name={"value[" + rowNumber + "][value]"}
                                    label={"Value "+ (rowNumber+1)}
                                    disabled={submitting}
                                    labelCol={{span: labelColSpan}}
                                    wrapperCol={{span: wrapperColSpan}}
                                    style={_processor_json && _processor_json.style || {}}
                                    fieldStyle={_processor_json && _processor_json.style || {}}
                                    labelStyle={_processor_json && _processor_json.style || {}}
                                />
                            </div>
                        </div>
                        <div style={{ marginLeft: '10px', display: 'flex', alignItems: 'center' }}>
                            <button
                                type="button"
                                onClick={() => that.removeRow(rowNumber)}
                                disabled={!canRemove}
                                style={{
                                    padding: '4px 10px',
                                    backgroundColor: canRemove ? '#ff4d4f' : '#f5f5f5',
                                    color: canRemove ? 'white' : '#d9d9d9',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: canRemove ? 'pointer' : 'not-allowed',
                                    fontSize: '12px'
                                }}
                                title={canRemove ? 'Remove this row' : 'Cannot remove the last row'}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                );
            }

            return output;
        }

        const getAssociatedRows = () => {
            let output = [];

            // Add column headers first
            output.push(
                <div style={{ display: 'flex', marginBottom: '5px' }} key="headers">
                    <div style={{ display: 'flex', flex: 1 }}>
                        {getColumnHeaders()}
                    </div>
                    {/* Add an empty space for the remove button column */}
                    <div style={{ width: '70px' }}></div>
                </div>
            );

            // Then add all rows
            for (let rcount = 0; rcount < this.state.rowCount; rcount ++)
            {
                output.push(getAssociatedRow(rcount));
            }
            return output;
        }

        function getUploadDisplay (imgValue)
        {
            if (imgValue.match('gif') || imgValue.match('png') || imgValue.match('jpeg') || imgValue.match('jpg'))
            {
                return <img style={LogoStyles} className={LogoStyles.settingLogo} src={imgValue}/>
            }
            else if (imgValue !== "/settings/files/")
            {
                let vArray = imgValue.split(".");
                return <a href={"/settings/files/" + imgValue} target="_new">{vArray[1]} file</a>;
            }

        }

        const setSettingValue = (fvalue) => {

            //this.props.value2 = fvalue;
            console.log(this.props);
            console.log(this);
            //this.props.form.setFieldsValue({
            //    ['value']: fvalue
            //});
        }

        const CKInputHandler = (event) => {

            //console.log(event, editor);
            //console.log(event.editor.getData());
            setSettingValue (event.editor.getData());

        };

        return (

            <div>

                {processor === 'AssociativeJSON' && (
                    <div>
                        {getAssociatedRows()}
                        <div style={{ marginTop: 16, marginBottom: 16 }}>
                            <button
                                type="button"
                                onClick={this.addRow}
                                style={{
                                    padding: '5px 15px',
                                    backgroundColor: '#1890ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Add {title ? title.replace('List', '').trim() : 'Row'}
                            </button>
                        </div>
                    </div>
                )}


                {processor === 'TextAreaField' && (
                    <Field
                        required
                        component={TextAreaField}
                        name="value"
                        label={"Setting Value"}
                        disabled={submitting}
                        autoComplete="off"
                        labelCol={{span: labelColSpan}}
                        wrapperCol={{span: wrapperColSpan}}
                        fieldCol={{span: 22}}
                        afterCol={{span: 2}}
                        autoSize={{
                            minRows: 5,
                            maxRows: 17,
                        }}
                        style={_processor_json && _processor_json.style || {}}
                        fieldStyle={_processor_json && _processor_json.style || {}}
                        labelStyle={_processor_json && _processor_json.style || {}}
                    />

                )}

                {processor === 'InputField' && (

                    <Field
                        component={InputField}
                        name="value"
                        label="Setting Value"
                        disabled={submitting}
                        labelCol={{span: labelColSpan}}
                        wrapperCol={{span: wrapperColSpan}}
                        style={_processor_json && _processor_json.style || {}}
                        fieldStyle={_processor_json && _processor_json.style || {}}
                        labelStyle={_processor_json && _processor_json.style || {}}
                    />

                )}

                {processor === 'SelectField' && (

                    <Field
                        required
                        component={SelectField}
                        name="value"
                        label="Setting Value"
                        disabled={submitting}
                        options={_processor_json}
                        labelCol={{span: labelColSpan}}
                        wrapperCol={{span: wrapperColSpan}}
                        style={_processor_json && _processor_json.style || {}}
                        fieldStyle={_processor_json && _processor_json.style || {}}
                        labelStyle={_processor_json && _processor_json.style || {}}
                    />

                )}

                {processor === 'CheckboxField' && (

                    //const poptions = JSON.parse(processor_json.options);
                    //Object.keys(poptions).forEach(function(key) {
                    //  poptions[key];
                    //});
                    <Field
                        component={CheckboxField}
                        name="value[]"
                        label="Setting Value"
                        text=""
                        disabled={submitting}
                        labelCol={{span: labelColSpan}}
                        wrapperCol={{span: wrapperColSpan}}
                        style={_processor_json && _processor_json.style || {}}
                        fieldStyle={_processor_json && _processor_json.style || {}}
                        labelStyle={_processor_json && _processor_json.style || {}}
                    />

                )}

                {processor === 'DisplayField' && (

                    //const poptions = JSON.parse(processor_json.options);
                    //Object.keys(poptions).forEach(function(key) {
                    //  poptions[key];
                    //});
                    <Field
                        component={InputField}
                        name="value"
                        label="Setting Value"
                        disabled="1"
                        labelCol={{span: labelColSpan}}
                        wrapperCol={{span: wrapperColSpan}}
                        style={_processor_json && _processor_json.style || {}}
                        fieldStyle={_processor_json && _processor_json.style || {}}
                        labelStyle={_processor_json && _processor_json.style || {}}
                    />

                )}

                {processor === 'RichTextEditor' && (
                    <Field
                        name="value"
                        component={RichTextEditorField}
                        style={_processor_json && _processor_json.style || {}}
                        fieldStyle={_processor_json && _processor_json.style || {}}
                        labelStyle={_processor_json && _processor_json.style || {}}
                    />
                )}

                {processor === 'FileUpload' && (

                    <React.Fragment>

                        {imgValue != logo && (
                            <div>
                                {getUploadDisplay(imgValue)}
                            </div>
                        )}

                        <br/>
                        <Upload
                            name='file'
                            customRequest={onUpload}
                            showUploadList={false}
                            className={LogoStyles.uploaderPointer}
                        >
                            <UploadOutlined/>
                            {' Upload new ' + title}
                        </Upload>

                        <br/><img style={LogoStyles} className={LogoStyles.settingLogo} src={logo}/>

                    </React.Fragment>

                )}


            </div>

        )
        // Danny look here
        // all I want to do is set the value that is posted to the create / update

        //                  <Field
        //                     component={InputField}
        //                     name="value"
        //                     value={logo}
        //                 />
        //                <input type="hidden" name="value" value={logo} />
        // ^^ If I put hidden field in using a component, the value does not get set.
    }

}

